import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
} from '@angular/core';
import { AccountClaimsMonitoringMonth } from '@rivethealth/data/account';
import { of } from 'rxjs/internal/observable/of';
import { ClaimsMonitoringService } from '../../api/claims-monitoring.service';
import { ChangeService } from '../../common/core/change.service';
import { HookDirective } from '../../common/core/hook';
import { useLoad, useSubscriptionLatest } from '../../common/core/hooks';
import { RouteService } from '../../common/state/route.service';
import { ListManager } from '../../common/super-table/list-manager';
import { LocalDataSource } from '../../common/super-table/local-data-source';
import { TableStateHelper } from '../../common/super-table/TableStateHelper';

const entryParams: LocalDataSource.StandardParams<AccountClaimsMonitoringMonth> = {
  sorters: {
    default: r => r.year_month,
    year_month: r => r.year_month,
  },
};

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'rv-rows large' },
  selector: 'claims-monitoring-detail-table',
  templateUrl: './detail-table.component.html',
  providers: [RouteService, ChangeService],
})
export class ClaimsMonitoringDetailTableComponent extends HookDirective {
  constructor(
    private readonly claimsService: ClaimsMonitoringService,
    private readonly routeService: RouteService,
    private readonly changeService: ChangeService,
    _cdr: ChangeDetectorRef,
  ) {
    super(_cdr);
    const tableStateHelper = new TableStateHelper<any>(
      this.table.filter,
      this.table.sort,
      this.changeService,
      'account-detail',
      'riveter',
    );
    tableStateHelper.tableSetup();
  }

  readonly table = new ListManager<{}>({
    defaultSort: {
      name: 'year_month',
      direction: 'asc',
    },
  });

  @Input() accountId: number;
  @Input() useServiceDate: boolean = true;

  get _() {
    const [
      accountMonitoringLoadable,
      reloadAccountMonitoringLoadable,
    ] = useLoad(() => {
      if (this.accountId) {
        return this.claimsService.getByAccountId(
          this.accountId,
          this.useServiceDate,
        );
      }
      return of(null);
    }, [this.accountId, this.useServiceDate]);

    const entries = useSubscriptionLatest(
      accountMonitoringLoadable.data
        ? this.table.load(
            LocalDataSource.standard(
              accountMonitoringLoadable.data.entries,
              entryParams,
            ),
          )
        : of(null),
      [accountMonitoringLoadable.data],
    );

    return {
      accountMonitoringLoadable,
      entries,
      table: this.table,
    };
  }
}
